import React, { useState } from "react";
import { useStepTop } from "@/lib/useStepTop";
import { DoneTick, StepCheer, useStepCheer } from "@/components/portal/StepCheer";
import { useNavigate, useOutletContext } from "react-router-dom";
import { api } from "@/lib/api";
import { gbp } from "@/lib/stages";
import { Note, PageHead, Panel, RailCard, DeskSupport, KeyValue } from "@/components/portal/crm/kit";
import { AdminPreviewBanner } from "./Portal";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { MoneyField, CountField, DEAL_PRESETS } from "@/components/ui/number-fields";
import { DocUploader } from "@/components/portal/DocUploader";
import { FastDocs } from "@/components/portal/FastDocs";
import { otherFiles, sendFile } from "@/lib/fastDocs";
import { CompanyLookup } from "@/components/portal/lookup/CompanyLookup";
import { DirectorPick, roleFor } from "@/components/portal/lookup/DirectorPick";
import { fillEmpty } from "@/lib/lookups";
import { AddressLookup } from "@/components/portal/lookup/AddressLookup";
import { FINANCE_TYPES, INDUSTRY_SECTORS, URGENCY_OPTIONS, COMPANY_ROLES } from "@/pages/hub/constants";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, Briefcase, Building2, CheckCircle2, Files, Loader2, Send, User,
} from "lucide-react";

const STEPS = [
  { key: "company", label: "Company", Icon: Building2 },
  { key: "applicant", label: "Applicant", Icon: User },
  { key: "deal", label: "The deal", Icon: Briefcase },
  { key: "docs", label: "Documents", Icon: Files },
  { key: "review", label: "Review", Icon: CheckCircle2 },
];

const COACH = {
  company: "Start with the business — search it and Connexis search fills in the rest. Revenue figures are optional, but they sharpen the match considerably.",
  applicant: "Tap your client from the company's directors, or type them in. The full name is all we truly need — email and phone help a desk come back to them faster.",
  deal: "The important bit: how much, and what for. Type any figure — we place deals from £25k to £500m+.",
  docs: "Bank statements and filed accounts get decisions back in hours instead of days. You can always add these later.",
  review: "Give it a final glance, add anything the desks should know, then submit.",
};

const SUGGESTED_DOCS = ["Management accounts", "Business plan or forecast", "Pitch deck", "Proof of address"];
const TERM_PRESETS = [6, 12, 24, 36, 60, 84, 120, 180, 240, 300];

const EMPTY = {
  applicant_name: "", applicant_email: "", applicant_phone: "", applicant_position: "",
  business_name: "", trading_name: "", company_number: "", vat_number: "",
  incorporation_date: "", industry_sector: "", employees: "", company_address: "", company_website: "",
  annual_revenue_gbp: "", monthly_revenue_gbp: "", net_profit_gbp: "",
  finance_type: "", amount_gbp: "", term_months: "",
  loan_purpose: "", security_offered: "",
  existing_debt: null, existing_debt_summary: "",
  personal_guarantee_available: null, urgency: "",
  notes: "", files: [],
};

export default function SubmitCase() {
  const { reload, isAdmin, projection, agent, pending } = useOutletContext();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [directors, setDirectors] = useState([]);
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const topRef = useStepTop(step);
  const cheer = useStepCheer(step, ["Company details in", "Applicant added", "Deal noted", "Documents sorted · final check"]);

  const canNext = () => {
    if (step === 0) return form.business_name.trim().length > 1;
    if (step === 1) return form.applicant_name.trim().length > 1;
    if (step === 2) return !!form.finance_type && parseFloat(form.amount_gbp) > 0;
    return true;
  };
  const ready = form.applicant_name.trim().length > 1 && form.business_name.trim().length > 1
    && !!form.finance_type && parseFloat(form.amount_gbp) > 0;

  const submit = async () => {
    setBusy(true);
    try {
      const payload = { ...form };
      for (const k of ["employees", "annual_revenue_gbp", "monthly_revenue_gbp", "net_profit_gbp", "amount_gbp", "term_months"]) {
        if (payload[k] === "" || payload[k] === null) delete payload[k];
        else payload[k] = parseFloat(payload[k]);
      }
      for (const k of Object.keys(payload)) if (payload[k] === "") payload[k] = null;
      payload.file_ids = (payload.files || []).map((f) => f.id);
      delete payload.files;
      const r = await api.post("/refer/leads", payload);
      toast.success("Case submitted. The desk is packaging it now.");
      await reload?.();
      nav(r.data?.id ? `/introducer/cases/${r.data.id}` : "/introducer");
    } catch (e) { toast.error(e?.response?.data?.detail || "Submission failed"); }
    finally { setBusy(false); }
  };

  const s = STEPS[step].key;

  if (pending) {
    return (
      <div data-testid="referrer-submit-page">
        <PageHead eyebrow="New introduction" title="Submit a case"
                  lede="Cases open up the moment the desk approves your account — usually within one working day."
                  testId="referrer-submit-head" />
        <Panel title="Not yet — but nearly" testId="referrer-submit-pending">
          <p style={{ fontSize: 14, color: "var(--mut)", lineHeight: 1.6, margin: 0 }}>
            Your account is under review. While you wait, set your notification preferences under <b>People, access &amp; setup</b>,
            read how a case moves in the guide, and have the first client's details ready: the business, what the money is for,
            how much, and the latest accounts or bank statements. You will get an email the moment the desk approves you.
          </p>
        </Panel>
      </div>
    );
  }

  return (
    <div data-testid="referrer-submit-page">
      {isAdmin && <div className="mb-6"><AdminPreviewBanner /></div>}

      <PageHead
        eyebrow="New introduction"
        title="Submit a case"
        lede="Five short steps — start with the business and we fill in what we can. Only the business, the applicant and the amount are needed today."
        testId="referrer-submit-head"
      />

      <div className="mt-7 grid items-start gap-7 xl:grid-cols-[minmax(0,1fr)_320px]">
        <div className="min-w-0 space-y-6" data-testid="referrer-submit-dialog">
          <div className="relative">
          <StepCheer cheer={cheer} at="calc(100% - 2px)" testId="refstep-cheer" />
          <div ref={topRef} className="-mx-1 flex scroll-mt-[78px] items-center gap-1.5 overflow-x-auto px-1 pb-1" data-testid="refstep-bar">
            {STEPS.map((st, i) => (
              <React.Fragment key={st.key}>
                <button
                  type="button" onClick={() => setStep(i)} data-testid={`refstep-${st.key}`}
                  className={`inline-flex h-10 shrink-0 items-center gap-2 rounded-[11px] border px-3.5 text-[12.5px] font-bold transition-colors ${
                    i === step ? "border-[#0B1220] bg-[#0B1220] text-white"
                      : i < step ? "border-[#8BD6BF] bg-[#D8F2E9] text-[#085B43]"
                        : "border-[#CBD5E1] bg-white text-slate-500 hover:border-[#0055FF] hover:text-[#0044CC]"}`}
                >
                  {i < step ? <DoneTick /> : <st.Icon className="size-3.5" />} {st.label}
                </button>
                {i < STEPS.length - 1 && <ArrowRight className="size-3.5 shrink-0 text-slate-300" />}
              </React.Fragment>
            ))}
          </div>
          </div>

          <Panel title={`Step ${step + 1} of ${STEPS.length} · ${STEPS[step].label}`} sub={COACH[s]} testId="submit-coach">
            {s === "applicant" && (
              <div className="grid gap-4 sm:grid-cols-2">
                <DirectorPick directors={directors} value={form.applicant_name} testId="ref-director" className="col-span-full"
                              title="Who's your client?" note="The company's directors, from Connexis search. Someone else? Type their details below."
                              onPick={(name, d) => setForm((f) => ({ ...f, applicant_name: name, applicant_position: roleFor(d.role) || f.applicant_position }))} />
                <F label="Full name *"><Input value={form.applicant_name} onChange={(e) => setField("applicant_name", e.target.value)} className="h-11" data-testid="ref-applicant-name" /></F>
                <F label="Position at company">
                  <Select value={form.applicant_position || undefined} onValueChange={(v) => setField("applicant_position", v)}>
                    <SelectTrigger className="h-11" data-testid="ref-position"><SelectValue placeholder="Pick…" /></SelectTrigger>
                    <SelectContent>{COMPANY_ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                  </Select>
                </F>
                <F label="Email"><Input type="email" value={form.applicant_email} onChange={(e) => setField("applicant_email", e.target.value)} className="h-11" data-testid="ref-applicant-email" /></F>
                <F label="Phone"><Input value={form.applicant_phone} onChange={(e) => setField("applicant_phone", e.target.value)} className="h-11" data-testid="ref-applicant-phone" /></F>
              </div>
            )}

            {s === "company" && (
              <div className="grid gap-4 sm:grid-cols-2">
                <CompanyLookup testId="ref-company-lookup" onPick={(c) => {
                  const vals = {
                    business_name: c.business_name, company_number: c.company_number, vat_number: c.vat_number,
                    incorporation_date: c.incorporation_date, company_address: c.company_address,
                    industry_sector: INDUSTRY_SECTORS.includes(c.industry_sector) ? c.industry_sector : null,
                    employees: c.employees, annual_revenue_gbp: c.annual_revenue_gbp,
                  };
                  setForm((f) => fillEmpty(f, vals).next);
                  setDirectors(c.directors || []);
                  return fillEmpty(form, vals).filled;
                }} />
                <F label="Legal name *"><Input value={form.business_name} onChange={(e) => setField("business_name", e.target.value)} className="h-11" data-testid="ref-business-name" /></F>
                <F label="Trading name"><Input value={form.trading_name} onChange={(e) => setField("trading_name", e.target.value)} className="h-11" /></F>
                <F label="Companies House #"><Input value={form.company_number} onChange={(e) => setField("company_number", e.target.value)} className="h-11" data-testid="ref-company-number" /></F>
                <F label="VAT number"><Input value={form.vat_number} onChange={(e) => setField("vat_number", e.target.value)} className="h-11" /></F>
                <F label="Incorporation date"><Input type="date" value={form.incorporation_date} onChange={(e) => setField("incorporation_date", e.target.value)} className="h-11" data-testid="ref-incorporation-date" /></F>
                <F label="Industry sector">
                  <Select value={form.industry_sector || undefined} onValueChange={(v) => setField("industry_sector", v)}>
                    <SelectTrigger className="h-11" data-testid="ref-industry"><SelectValue placeholder="Pick a sector…" /></SelectTrigger>
                    <SelectContent className="max-h-72">{INDUSTRY_SECTORS.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                  </Select>
                </F>
                <F label="Employees"><CountField value={form.employees} onChange={(v) => setField("employees", v)} suffix="people" max={500000} testId="ref-employees" /></F>
                <F label="Website"><Input value={form.company_website} onChange={(e) => setField("company_website", e.target.value)} className="h-11" /></F>
                <AddressLookup testId="ref-address-lookup" onPick={(a) => setField("company_address", a.line)} />
                <F label="Registered address" full><Input value={form.company_address} onChange={(e) => setField("company_address", e.target.value)} className="h-11" data-testid="ref-company-address" /></F>
                <F label="Annual revenue"><MoneyField value={form.annual_revenue_gbp} onChange={(v) => setField("annual_revenue_gbp", v)} ladder="revenue" compact testId="ref-annual-revenue" hint="drag or type" /></F>
                <F label="Monthly revenue"><MoneyField value={form.monthly_revenue_gbp} onChange={(v) => setField("monthly_revenue_gbp", v)} ladder="revenue" compact testId="ref-monthly-revenue" hint="drag or type" /></F>
                <F label="Net profit"><MoneyField value={form.net_profit_gbp} onChange={(v) => setField("net_profit_gbp", v)} ladder="revenue" compact testId="ref-net-profit" hint="drag or type" /></F>
              </div>
            )}

            {s === "deal" && (
              <div className="grid gap-4 sm:grid-cols-2">
                <F label="Finance type *">
                  <Select value={form.finance_type || undefined} onValueChange={(v) => setField("finance_type", v)}>
                    <SelectTrigger className="h-11" data-testid="ref-finance-type"><SelectValue placeholder="Pick…" /></SelectTrigger>
                    <SelectContent className="max-h-72">{FINANCE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </F>
                <F label="Urgency">
                  <Select value={form.urgency || undefined} onValueChange={(v) => setField("urgency", v)}>
                    <SelectTrigger className="h-11" data-testid="ref-urgency"><SelectValue placeholder="Pick…" /></SelectTrigger>
                    <SelectContent>{URGENCY_OPTIONS.map((u) => <SelectItem key={u.key} value={u.key}>{u.label}</SelectItem>)}</SelectContent>
                  </Select>
                </F>
                <F label="Amount requested *" full>
                  <MoneyField value={form.amount_gbp} onChange={(v) => setField("amount_gbp", v)} ladder="deal" presets={DEAL_PRESETS}
                              placeholder="250,000" testId="ref-amount" hint="drag or type — we place £25k to £500m+" />
                </F>
                <F label="Term" full>
                  <div className="flex flex-wrap items-center gap-2">
                    {TERM_PRESETS.map((m) => (
                      <button key={m} type="button" onClick={() => setField("term_months", String(m))}
                              className={`h-9 rounded-[10px] border px-3 text-[12.5px] font-bold transition-colors ${
                                parseInt(form.term_months, 10) === m ? "border-[#0055FF] bg-[#E4ECFF] text-[#0A3DB8]" : "border-[#CBD5E1] bg-white text-slate-600 hover:border-[#0055FF]"}`}
                              data-testid={`ref-term-chip-${m}`}>{m} mo</button>
                    ))}
                    <CountField value={form.term_months} onChange={(v) => setField("term_months", v)} min={1} max={600} suffix="months" testId="ref-term-input" />
                  </div>
                </F>
                <F label="Loan purpose" full>
                  <Textarea value={form.loan_purpose} onChange={(e) => setField("loan_purpose", e.target.value)} className="min-h-[70px]" placeholder="e.g. Working capital to expand the fleet ahead of a Q4 contract." data-testid="ref-purpose" />
                </F>
                <F label="Security offered" full>
                  <Textarea value={form.security_offered} onChange={(e) => setField("security_offered", e.target.value)} className="min-h-[70px]" placeholder="e.g. First charge over 12 Rockingham Road (~£1.2m OMV) + directors' PGs." />
                </F>
                <F label="Existing debt?"><Tri name="existing_debt" value={form.existing_debt} onChange={(v) => setField("existing_debt", v)} /></F>
                <F label="Personal guarantee available?"><Tri name="pg" value={form.personal_guarantee_available} onChange={(v) => setField("personal_guarantee_available", v)} /></F>
                {form.existing_debt === true && (
                  <F label="Existing debt summary" full>
                    <Textarea value={form.existing_debt_summary} onChange={(e) => setField("existing_debt_summary", e.target.value)} className="min-h-[70px]" placeholder="Provider · balance · monthly · security" />
                  </F>
                )}
              </div>
            )}

            {s === "docs" && (
              <div className="space-y-5">
                <FastDocs financeType={form.finance_type} files={form.files} testId="ref-fast-docs"
                          foot="Optional — submit without them and chase your client later; they get a private upload link."
                          onUpload={async (label, file, onProgress) => {
                            const rec = await sendFile("/hub/files/upload", file, { label }, onProgress);
                            setForm((f) => ({ ...f, files: [...f.files, rec] }));
                          }}
                          onRemove={(id) => setForm((f) => ({ ...f, files: f.files.filter((x) => x.id !== id) }))} />
                <div>
                  <div className="mb-2 text-[12.5px] font-semibold text-slate-600">Anything else</div>
                  <DocUploader files={otherFiles(form.finance_type, form.files).others} suggestions={SUGGESTED_DOCS} testId="ref-doc-dropzone"
                               onChange={(fs) => setForm((f) => ({ ...f, files: otherFiles(f.finance_type, f.files).keep(fs) }))} />
                </div>
              </div>
            )}

            {s === "review" && (
              <div className="space-y-1">
                <KeyValue label="Applicant" value={form.applicant_name || "—"} />
                <KeyValue label="Business" value={form.business_name || "—"} />
                <KeyValue label="Sector" value={form.industry_sector || "—"} />
                <KeyValue label="Finance" value={form.finance_type ? `${form.finance_type} · ${gbp(form.amount_gbp)}` : "—"} />
                <KeyValue label="Documents" value={`${form.files.length} attached`} />
                <div className="pt-4">
                  <F label="Anything else the desks should know?" full>
                    <Textarea value={form.notes} onChange={(e) => setField("notes", e.target.value)} className="min-h-[90px]" placeholder="Optional…" data-testid="ref-notes" />
                  </F>
                </div>
              </div>
            )}

            <div className="mt-6 flex items-center justify-between gap-3 border-t border-[#E2E8F0] pt-5">
              <Button variant="outline" onClick={() => setStep((v) => Math.max(0, v - 1))} disabled={step === 0}
                      className="h-11 rounded-[11px] px-4 text-[13px] font-bold" data-testid="refstep-back">
                <ArrowLeft className="mr-1.5 size-3.5" /> Back
              </Button>
              {step < STEPS.length - 1 ? (
                <Button onClick={() => setStep((v) => Math.min(STEPS.length - 1, v + 1))} disabled={!canNext()}
                        className="h-11 rounded-[11px] bg-[#0055FF] px-5 text-[13.5px] font-bold hover:bg-[#0044CC]" data-testid="refstep-next">
                  Next <ArrowRight className="ml-1.5 size-3.5" />
                </Button>
              ) : (
                <Button onClick={submit} disabled={busy || !ready || isAdmin}
                        className="h-11 rounded-[11px] bg-[#0B6B4F] px-5 text-[13.5px] font-bold hover:bg-[#085B43]" data-testid="referrer-submit-final">
                  {busy ? <Loader2 className="size-4 animate-spin" /> : <>Submit this case <Send className="ml-1.5 size-3.5" /></>}
                </Button>
              )}
            </div>
          </Panel>
        </div>

        <div className="space-y-4">
          <RailCard title="What the desk will see" testId="referrer-submit-summary">
            <KeyValue label="Applicant" value={form.applicant_name || "—"} />
            <KeyValue label="Business" value={form.business_name || "—"} />
            <KeyValue label="Finance" value={form.finance_type || "—"} />
            <KeyValue label="Amount" value={form.amount_gbp ? gbp(form.amount_gbp) : "—"} />
            <KeyValue label="Term" value={form.term_months ? `${form.term_months} months` : "—"} />
            <KeyValue label="Documents" value={String(form.files.length)} />
            <div className="mt-3 border-t border-[#EFF3F8] pt-3 text-[12.5px] leading-relaxed text-slate-500">
              {ready ? "Ready to submit. We'll route it to the desks whose criteria it actually meets."
                : "Applicant name, legal business name, finance type and amount are the four we can't route without."}
            </div>
          </RailCard>

          <DeskSupport body="Not sure whether a case will fly? Send it anyway and ask in the thread — we would rather tell you early." chat={!agent} />
          <Note>{projection?.note || "Fees vary from lender to lender. Anything we show you is a range, not a quote."}</Note>
        </div>
      </div>
    </div>
  );
}

const F = ({ label, children, full = false }) => (
  <div className={full ? "sm:col-span-2" : ""}>
    <Label className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-slate-500">{label}</Label>
    <div className="mt-1.5">{children}</div>
  </div>
);

const Tri = ({ name, value, onChange }) => (
  <div className="flex h-11 items-center gap-4 text-[13px]">
    {[["Yes", true], ["No", false], ["Unknown", null]].map(([l, v]) => (
      <label key={l} className="inline-flex cursor-pointer items-center gap-1.5 font-semibold text-slate-600">
        <input type="radio" name={name} checked={value === v} onChange={() => onChange(v)} className="accent-[#0055FF]" />{l}
      </label>
    ))}
  </div>
);
